import React, { useEffect } from 'react';

import { useMediaQuery, ButtonBase } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReactComponent as MenuIcon } from 'assets/icons/menu.svg';
import { ReactComponent as LogoFull } from 'assets/logo_light.svg';
import { ReactComponent as LogoSymbol } from 'assets/logo_symbol.svg';
import MenuSection from 'components/Layout/_components/MenuSection';
import ContentSection from 'components/Layout/_components/sections/ContentSection/ContentSection';
import FooterSection from 'components/Layout/_components/sections/FooterSection/FooterSection';
import OrganizationSection from 'components/Layout/_components/sections/OrganizationSection/OrganizationSection';
import PocketSafeAdministrationSection from 'components/Layout/_components/sections/PocketSafeAdministrationSection/PocketSafeAdministrationSection';
import ServiceSection from 'components/Layout/_components/sections/ServiceSection/ServiceSection';
import UserSection from 'components/Layout/_components/sections/UserSection/UserSection';
import menu_messages from 'messages/menu_messages';
import { useAuthContext } from 'reactContext/AuthContext';
import PATHS from 'router/PATHS';
import useBrandingStore from 'storages/brandingStore';
import useLayoutStore from 'storages/layoutStore';
import { DRAWER_WIDTH } from 'styles/consts';

import DashboardsSection from '../sections/DashboardsSection/DashboardsSection';

const MENU_BACKGROUND = '#101828';
const MENU_BACKGROUND_SOFT = '#14233A';
const MENU_BACKGROUND_DEEP = '#0C1727';

const useStyles = makeStyles(theme => ({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  drawer: {
    width: DRAWER_WIDTH.OPEN,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    background: `linear-gradient(180deg, ${MENU_BACKGROUND} 0%, ${MENU_BACKGROUND_SOFT} 54%, ${MENU_BACKGROUND_DEEP} 100%)`,
    color: '#F8FBFF',
    overflowX: 'hidden',
    borderRight: 'none',
    boxShadow: '12px 0 32px rgba(16, 24, 40, 0.12)',
    '&.MuiDrawer-paper, & .MuiDrawer-paper': {
      background: `linear-gradient(180deg, ${MENU_BACKGROUND} 0%, ${MENU_BACKGROUND_SOFT} 54%, ${MENU_BACKGROUND_DEEP} 100%)`,
      color: '#F8FBFF',
    },
  },
  drawerInner: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    background: `linear-gradient(180deg, ${MENU_BACKGROUND} 0%, ${MENU_BACKGROUND_SOFT} 54%, ${MENU_BACKGROUND_DEEP} 100%)`,
  },
  drawerMainSection: {
    flexGrow: '1',
    marginBottom: '-2px',
    background: 'transparent',
  },
  navigation: {
    minHeight: '100%',
    background: 'transparent',
  },
  drawerOpen: {
    width: DRAWER_WIDTH.OPEN,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: DRAWER_WIDTH.CLOSED,
  },

  icon: {
    color: '#F8FBFF',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'sticky',
    top: 0,
    background: `linear-gradient(180deg, ${MENU_BACKGROUND} 0%, rgba(16, 24, 40, 0.94) 100%)`,
    zIndex: 1,
    flexDirection: ({ isOpen }) => (isOpen ? 'row' : 'column'),
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)',
  },
  logo: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexGrow: 1,
  },
  logoFull: {
    width: '102px',
    height: '16px',
    animationName: '$fadeIn',
    animationDuration: 300,
  },
  logoSymbol: {
    animationName: '$fadeIn',
    animationDuration: 300,
  },
  searchButton: {
    order: ({ isOpen }) => (isOpen ? '2' : '1'),
    color: '#F8FBFF',
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: 'rgba(104, 197, 248, 0.16)',
      },
    },
  },
  focusableControl: {
    '&.Mui-focusVisible, &:focus-visible': {
      outline: `3px solid ${theme.palette.grey[300]}`,
      outlineOffset: 2,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
}));

const FullLogoWithFallback = ({ className, logo }) => {
  if (logo?.full) return <img alt='' className={className} src={logo.full} />;
  if (logo?.symbol) return <img alt='' className={className} src={logo.symbol} />;
  return <LogoFull className={className} />;
};

const SymbolLogoWithFallback = ({ className, logo }) => {
  if (logo?.symbol) return <img alt='' className={className} src={logo.symbol} />;
  if (logo?.full) return <img alt='' className={className} src={logo.full} />;
  return <LogoSymbol className={className} />;
};

const MainMenuDesktop = () => {
  const { t } = useTranslation();
  const { logo } = useBrandingStore();
  const { isAuthorized } = useAuthContext();
  const downLg = useMediaQuery('@media screen and (max-width: 1200px)');

  const { isMenuOpen, closeMenu, toggleMenu } = useLayoutStore();

  useEffect(() => {
    if (downLg) closeMenu();
  }, [downLg]);

  const styles = useStyles({ isOpen: isMenuOpen });
  return (
    <Drawer
      classes={{
        paper: clsx(
          {
            [styles.drawerOpen]: isMenuOpen,
            [styles.drawerClose]: !isMenuOpen,
          },
          styles.drawer,
        ),
      }}
      className={clsx(styles.drawer, {
        [styles.drawerOpen]: isMenuOpen,
        [styles.drawerClose]: !isMenuOpen,
      })}
      variant='permanent'
    >
      <div className={styles.drawerInner}>
        <div className={styles.drawerMainSection}>
          <MenuSection className={styles.toolbar} collapsible={false} keepVisible>
            <ButtonBase
              aria-label={t(menu_messages.go_to_website)}
              className={clsx(styles.logo, styles.focusableControl)}
              component='a'
              href={process.env.REACT_APP_LANDING_PAGE_URL}
              target='_blank'
            >
              {isMenuOpen ? (
                <FullLogoWithFallback className={styles.logoFull} logo={logo} />
              ) : (
                <SymbolLogoWithFallback className={clsx('MuiSvgIcon-root', styles.logoSymbol, styles.icon)} logo={logo} />
              )}
            </ButtonBase>
            <IconButton
              aria-label={t(menu_messages.global_search)}
              className={clsx(styles.searchButton, styles.focusableControl)}
              component={Link}
              to={PATHS.GLOBAL_SEARCH}
            >
              <SearchIcon className={styles.icon} />
            </IconButton>
            <IconButton
              aria-controls='desktop-main-navigation'
              aria-expanded={isMenuOpen}
              aria-label={t(isMenuOpen ? menu_messages.close_navigation : menu_messages.open_navigation)}
              className={styles.focusableControl}
              data-cy='menu_hamburger'
              onClick={toggleMenu}
            >
              <MenuIcon className={styles.icon} />
            </IconButton>
          </MenuSection>
          <nav aria-label={t(menu_messages.main_navigation)} className={styles.navigation} id='desktop-main-navigation'>
            <UserSection />
            {isAuthorized && (
              <>
                <ServiceSection />
                <OrganizationSection />
                <ContentSection />
                <DashboardsSection />
                <PocketSafeAdministrationSection />
              </>
            )}
          </nav>
        </div>
        <FooterSection />
      </div>
    </Drawer>
  );
};

export default MainMenuDesktop;
